import { useState, useEffect, useCallback } from "react";
import { Package, ArrowLeft, RefreshCw, Trash2, Eye, Mail, Phone, CheckCircle, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "reviewed", label: "Reviewed", color: "bg-yellow-100 text-yellow-800" },
  { value: "contacted", label: "Contacted", color: "bg-purple-100 text-purple-800" },
  { value: "completed", label: "Completed", color: "bg-green-100 text-green-800" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-800" }
];

const StatusBadge = ({ status }) => {
  const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  return (
    <Badge className={`${statusConfig.color} font-medium`} data-testid={`status-badge-${status}`}>
      {statusConfig.label}
    </Badge>
  );
};

const StatusIcon = ({ status }) => {
  switch(status) {
    case "completed": return <CheckCircle className="w-4 h-4 text-green-600" />;
    case "rejected": return <XCircle className="w-4 h-4 text-red-600" />;
    default: return <Clock className="w-4 h-4 text-gray-400" />;
  }
};

export default function AdminDashboard() {
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setIsLoading(false);
    }
  }, []); // API, axios, toast are stable module-level imports

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Tell search engines not to index the admin dashboard
  useEffect(() => {
    const metaRobots = document.createElement("meta");
    metaRobots.name = "robots";
    metaRobots.content = "noindex, nofollow";
    document.head.appendChild(metaRobots);
    document.title = "Admin Dashboard | CashForTestStripsJax";
    return () => {
      metaRobots.remove();
      document.title = "CashForTestStripsJax";
    };
  }, []);

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      await axios.patch(`${API}/submissions/${submissionId}/status`, { status: newStatus });
      setSubmissions(prev => 
        prev.map(s => s.id === submissionId ? { ...s, status: newStatus } : s)
      );
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (submissionId) => {
    try {
      await axios.delete(`${API}/submissions/${submissionId}`);
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      toast.success("Submission deleted");
    } catch (error) {
      toast.error("Failed to delete submission");
    }
  };

  const viewDetails = (submission) => {
    setSelectedSubmission(submission);
    setIsDetailOpen(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatExpiration = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Stats
  const stats = {
    total: submissions.length,
    new: submissions.filter(s => s.status === 'new').length,
    completed: submissions.filter(s => s.status === 'completed').length,
    totalBoxes: submissions.reduce((acc, s) => acc + s.quantity, 0)
  };

  // Render submissions content based on state
  const renderSubmissionsContent = () => {
    if (isLoading) {
      return (
        <div className="p-12 text-center text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading submissions...</p>
        </div>
      );
    }

    if (submissions.length === 0) {
      return (
        <div className="p-12 text-center text-gray-500" data-testid="empty-state">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No submissions yet</p>
          <p className="text-sm">Submissions will appear here when customers fill out the form.</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">Date</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead className="text-center">Qty</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {submissions.map((submission) => (
              <TableRow key={submission.id} data-testid={`submission-row-${submission.id}`}>
                <TableCell className="text-sm text-gray-500">
                  {formatDate(submission.created_at)}
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium text-gray-900">{submission.contact_name}</p>
                    <p className="text-sm text-gray-500">{submission.contact_email}</p>
                  </div>
                </TableCell>
                <TableCell className="font-medium">{submission.brand}</TableCell>
                <TableCell className="text-center font-semibold">{submission.quantity}</TableCell>
                <TableCell className="text-sm">{formatExpiration(submission.expiration_date)}</TableCell>
                <TableCell>
                  <Select 
                    value={submission.status} 
                    onValueChange={(value) => handleStatusChange(submission.id, value)}
                  >
                    <SelectTrigger className="w-[130px] h-8" data-testid={`status-select-${submission.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => viewDetails(submission)}
                      data-testid={`view-btn-${submission.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" data-testid={`delete-btn-${submission.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Submission?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete the submission from {submission.contact_name}. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(submission.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Site</span>
            </Link>
            <div className="h-6 w-px bg-gray-200" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0F5132] rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-bold text-lg text-[#1F2937]" style={{ fontFamily: 'Manrope' }}>Admin Dashboard</h1>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchSubmissions} 
            disabled={isLoading}
            className="gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { id: "total", label: "Total Submissions", value: stats.total, color: "text-gray-900" },
            { id: "pending", label: "New / Pending", value: stats.new, color: "text-blue-600" },
            { id: "completed", label: "Completed", value: stats.completed, color: "text-green-600" },
            { id: "boxes", label: "Total Boxes", value: stats.totalBoxes, color: "text-purple-600" }
          ].map((stat) => (
            <div key={stat.id} className="bg-white rounded-xl p-5 border border-gray-100" data-testid={`stat-${stat.id}`}>
              <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
              <p className={`text-3xl font-bold ${stat.color}`} style={{ fontFamily: 'Manrope' }}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              Submissions
            </h2>
          </div>

          {renderSubmissionsContent()}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
              Submission Details
            </DialogTitle>
            <DialogDescription>
              Submitted on {selectedSubmission && formatDate(selectedSubmission.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-6 mt-4">
              {/* Status */}
              <div className="flex items-center gap-3">
                <StatusIcon status={selectedSubmission.status} />
                <StatusBadge status={selectedSubmission.status} />
              </div>

              {/* Product Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>Product Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Brand</p>
                    <p className="font-medium">{selectedSubmission.brand}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Quantity</p>
                    <p className="font-medium">{selectedSubmission.quantity} boxes</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Condition</p>
                    <p className="font-medium capitalize">{selectedSubmission.condition}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Expires</p>
                    <p className="font-medium">{formatExpiration(selectedSubmission.expiration_date)}</p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>Contact Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {selectedSubmission.contact_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="font-medium">{selectedSubmission.contact_name}</p>
                  </div>
                  <a 
                    href={`mailto:${selectedSubmission.contact_email}`}
                    className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    {selectedSubmission.contact_email}
                  </a>
                  {selectedSubmission.contact_phone && (
                    <a 
                      href={`tel:${selectedSubmission.contact_phone}`}
                      className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                    >
                      <Phone className="w-4 h-4" />
                      {selectedSubmission.contact_phone}
                    </a>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedSubmission.notes && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>Notes</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                    {selectedSubmission.notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
